<template>
  <div class="page">
    <div v-if="trackingNo" class="success-box">
      <h1>{{ t('successTitle') }}</h1>
      <div class="tracking">{{ trackingNo }}</div>
      <p>{{ t('successLead') }}</p>
      <p>{{ t('successMail') }}</p>
      <pgp-meta />
      <router-link class="btn btn-primary" to="/">{{ t('backHome') }}</router-link>
    </div>
    <div v-else class="submit-layout">
      <div class="submit-main">
      <fieldset :disabled="!portalOpen" style="border:0;padding:0;margin:0;min-width:0">
      <h1>{{ t('submitTitle') }}</h1>
      <div class="hint">
        <span>{{ encryptReminder }}</span>
        <a class="hint-link" href="/.well-known/pgp-key.asc">{{ t('downloadPgp') }}</a>
      </div>
      <p v-if="!portalOpen" class="error">{{ t('portalClosed') }}</p>
      <div class="steps">
        <div class="step" :class="{ on: step === 1 }">01 {{ t('stepContent') }}</div>
        <div class="step" :class="{ on: step === 2 }">02 {{ t('stepRisk') }}</div>
        <div class="step" :class="{ on: step === 3 }">03 {{ t('stepContact') }}</div>
      </div>

      <div v-show="step === 1" class="form-grid">
        <label class="field">
          <span>{{ t('typeLabel') }}</span>
          <select v-model="form.type">
            <option value="vulnerability">{{ t('typeVuln') }}</option>
            <option value="incident">{{ t('typeIncident') }}</option>
          </select>
        </label>
        <label class="field">
          <span>{{ t('title') }} *</span>
          <span class="control">
            <input v-model="form.title" maxlength="255" :class="{ invalid: errors.title }" />
            <em v-if="errors.title" class="field-error">{{ errors.title }}</em>
          </span>
        </label>
        <label class="field">
          <span>{{ t('severity') }}</span>
          <select v-model="form.severity">
            <option value="critical">{{ t('sevCritical') }}</option>
            <option value="high">{{ t('sevHigh') }}</option>
            <option value="medium">{{ t('sevMedium') }}</option>
            <option value="low">{{ t('sevLow') }}</option>
          </select>
        </label>
        <label class="field">
          <span>{{ t('product') }} *</span>
          <span class="control">
            <input v-model="form.affectedProduct" maxlength="512" :placeholder="t('productPh')" :class="{ invalid: errors.affectedProduct }" />
            <em v-if="errors.affectedProduct" class="field-error">{{ errors.affectedProduct }}</em>
          </span>
        </label>
        <label class="field top">
          <span>{{ t('description') }} *</span>
          <span class="control">
            <textarea v-model="form.description" rows="6" maxlength="8000" :class="{ invalid: errors.description }" />
            <em v-if="errors.description" class="field-error">{{ errors.description }}</em>
          </span>
        </label>
        <label v-if="form.type === 'vulnerability'" class="field top">
          <span>{{ t('poc') }}</span>
          <textarea v-model="form.pocSteps" rows="5" maxlength="8000" />
        </label>
        <template v-else>
          <label class="field">
            <span>{{ t('incidentTime') }}</span>
            <input type="datetime-local" v-model="form.incidentTime" />
          </label>
          <label class="field top">
            <span>{{ t('impact') }}</span>
            <textarea v-model="form.impactScope" rows="4" maxlength="2000" />
          </label>
        </template>
      </div>

      <div v-show="step === 2" class="form-grid">
        <p class="field-note">{{ t('craHint') }}</p>
        <label class="field">
          <span>{{ t('exploited') }}</span>
          <select v-model="form.activelyExploited">
            <option value="unknown">{{ t('unknown') }}</option>
            <option value="yes">{{ t('yes') }}</option>
            <option value="no">{{ t('no') }}</option>
          </select>
        </label>
        <label class="field">
          <span>{{ t('malware') }}</span>
          <select v-model="form.maliciousCodePossible">
            <option value="unknown">{{ t('unknown') }}</option>
            <option value="yes">{{ t('yes') }}</option>
            <option value="no">{{ t('no') }}</option>
          </select>
        </label>
        <div class="field top">
          <span>{{ t('evidence') }}</span>
          <div class="checks">
            <label v-for="item in evidenceOptions" :key="item.v"><input type="checkbox" :value="item.v" v-model="evidence"> {{ t(item.k) }}</label>
          </div>
        </div>
        <div class="field top">
          <span>{{ t('dimensions') }}</span>
          <div class="checks">
            <label v-for="item in dimOptions" :key="item.v"><input type="checkbox" :value="item.v" v-model="dims"> {{ t(item.k) }}</label>
          </div>
        </div>
        <label class="field">
          <span>{{ t('regions') }}</span>
          <input v-model="form.affectedRegions" maxlength="512" />
        </label>
        <label class="field">
          <span>{{ t('sensitivity') }}</span>
          <select v-model="form.informationSensitivity">
            <option value="normal">{{ t('senNormal') }}</option>
            <option value="sensitive">{{ t('senSensitive') }}</option>
            <option value="high">{{ t('senHigh') }}</option>
          </select>
        </label>
        <label class="field top">
          <span>{{ t('mitigation') }}</span>
          <textarea v-model="form.userMitigation" rows="4" maxlength="2000" />
        </label>
      </div>

      <div v-show="step === 3" class="form-grid">
        <label class="field">
          <span>{{ t('anonymous') }}</span>
          <input class="check-only" type="checkbox" :true-value="1" :false-value="0" v-model="form.isAnonymous">
        </label>
        <template v-if="form.isAnonymous !== 1">
          <label class="field">
            <span>{{ t('reporterName') }}</span>
            <input v-model="form.reporterName" maxlength="255" />
          </label>
          <label class="field">
            <span>{{ t('reporterEmail') }} *</span>
            <span class="control">
              <input type="email" v-model="form.reporterEmail" maxlength="255" :class="{ invalid: errors.reporterEmail }" />
              <em v-if="errors.reporterEmail" class="field-error">{{ errors.reporterEmail }}</em>
            </span>
          </label>
        </template>
        <div class="field top">
          <span>{{ t('captcha') }} *</span>
          <span class="control">
            <span class="captcha-line">
              <img v-if="captcha.image" class="captcha-img" :src="captcha.image" alt="" />
              <input v-model="captcha.answer" maxlength="3" :placeholder="t('captchaHint')" :disabled="!!captcha.token" :class="{ invalid: errors.captcha }" />
            </span>
            <em v-if="errors.captcha" class="field-error">{{ errors.captcha }}</em>
            <span class="form-actions tight">
              <button class="btn btn-outline" type="button" @click="refreshCaptcha">{{ t('captchaRefresh') }}</button>
              <button class="btn btn-primary" type="button" :disabled="!!captcha.token" @click="verifyCaptcha">{{ captcha.token ? t('captchaOk') : t('captcha') }}</button>
            </span>
          </span>
        </div>
        <div class="field top">
          <span>{{ t('attach') }}</span>
          <span class="control">
            <p class="field-note">{{ t('attachHint') }}</p>
            <p v-if="!captcha.token" class="error">{{ t('attachNeedCaptcha') }}</p>
            <input type="file" :disabled="!captcha.token || !portalOpen" @change="onFile" />
            <ul>
              <li v-for="file in files" :key="file.id">{{ file.originalName }} ({{ file.scanStatus }})</li>
            </ul>
          </span>
        </div>
      </div>

      <p v-if="error" class="error">{{ error }}</p>
      <div class="form-actions">
        <button v-if="step > 1" class="btn btn-outline" type="button" @click="step -= 1">{{ t('prev') }}</button>
        <button v-if="step < 3" class="btn btn-primary" type="button" @click="next">{{ t('next') }}</button>
        <button v-if="step === 3" class="btn btn-primary" type="button" :disabled="!canSubmit" @click="submit">{{ t('submitBtn') }}</button>
      </div>
      </fieldset>
      </div>
      <aside class="submit-aside">
        <mail-channel />
      </aside>
    </div>
  </div>
</template>
<script>
import { t } from '../i18n'
import { mapState, mapGetters } from 'vuex'
import { portalApi } from '../api/portalApi'
import MailChannel from '../components/MailChannel.vue'
import PgpMeta from '../components/PgpMeta.vue'

export default {
  name: 'Submit',
  components: { MailChannel, PgpMeta },
  data() {
    return {
      step: 1,
      trackingNo: '',
      error: '',
      evidence: [],
      dims: [],
      files: [],
      errors: {},
      captcha: { challengeId: '', image: '', answer: '', token: '' },
      form: {
        type: 'vulnerability',
        title: '',
        severity: 'medium',
        affectedProduct: '',
        description: '',
        pocSteps: '',
        incidentTime: '',
        impactScope: '',
        activelyExploited: 'unknown',
        maliciousCodePossible: 'unknown',
        affectedRegions: '',
        informationSensitivity: 'normal',
        userMitigation: '',
        isAnonymous: 0,
        reporterName: '',
        reporterEmail: ''
      },
      evidenceOptions: [
        { v: 'selfMonitorAlert', k: 'evSelfMonitor' },
        { v: 'artifactCapture', k: 'evArtifact' },
        { v: 'publicPocSeen', k: 'evPublicPoc' },
        { v: 'thirdPartyNotice', k: 'evThirdParty' },
        { v: 'fieldInstance', k: 'evFieldInstance' },
        { v: 'customerReport', k: 'evCustomer' },
        { v: 'inferenceOnly', k: 'evInference' },
        { v: 'other', k: 'evOther' }
      ],
      dimOptions: [
        { v: 'dataExposure', k: 'dimExposure' },
        { v: 'dataTamper', k: 'dimTamper' },
        { v: 'serviceDisrupt', k: 'dimServiceDisrupt' },
        { v: 'identityForge', k: 'dimIdentityForge' },
        { v: 'physicalSafety', k: 'dimPhysicalSafety' },
        { v: 'supplyChain', k: 'dimSupplyChain' },
        { v: 'privacyImpact', k: 'dimPrivacy' },
        { v: 'businessContinuity', k: 'dimBizContinuity' }
      ]
    }
  },
  computed: {
    ...mapState(['locale', 'brand']),
    ...mapGetters(['encryptReminder']),
    portalOpen() {
      const enabled = this.brand && this.brand.portalEnabled
      return enabled === 1 || enabled === true || enabled === '1'
    },
    canSubmit() {
      return this.portalOpen && !!this.captcha.token
    }
  },
  methods: {
    t(key) {
      return t(this.locale, key)
    },
    next() {
      this.error = ''
      if (this.step === 1 && !this.validateContent()) {
        return
      }
      this.errors = {}
      this.step += 1
      if (this.step === 3 && !this.captcha.image) {
        this.refreshCaptcha()
      }
    },
    validateContent() {
      const errors = {}
      this.requireField(errors, 'title', this.form.title)
      this.requireField(errors, 'affectedProduct', this.form.affectedProduct)
      this.requireField(errors, 'description', this.form.description)
      this.errors = errors
      return Object.keys(errors).length === 0
    },
    requireField(errors, key, value) {
      if (!String(value || '').trim()) {
        errors[key] = this.t('required')
      }
    },
    refreshCaptcha() {
      this.captcha.token = ''
      this.captcha.answer = ''
      portalApi.issueCaptcha().then((data) => {
        this.captcha.challengeId = data.challengeId
        this.captcha.image = data.image
      }).catch((e) => {
        this.error = e.message
      })
    },
    verifyCaptcha() {
      this.error = ''
      portalApi.verifyCaptcha({ challengeId: this.captcha.challengeId, answer: this.captcha.answer }).then((data) => {
        this.captcha.token = data.captchaToken
      }).catch((e) => {
        this.error = e.message
        this.refreshCaptcha()
      })
    },
    onFile(ev) {
      const file = ev.target.files && ev.target.files[0]
      ev.target.value = ''
      if (!file || !this.captcha.token) {
        return
      }
      portalApi.uploadAttachment(this.captcha.token, file).then((data) => {
        this.files.push(data)
      }).catch((e) => {
        this.error = e.message
      })
    },
    submit() {
      this.error = ''
      if (!this.validateContent()) {
        this.step = 1
        return
      }
      const errors = {}
      if (this.form.isAnonymous !== 1) {
        const email = String(this.form.reporterEmail || '').trim()
        if (!email) {
          errors.reporterEmail = this.t('required')
        } else if (email.indexOf('@') < 1) {
          errors.reporterEmail = this.t('emailInvalid')
        }
      }
      if (!this.captcha.token) {
        errors.captcha = this.t('attachNeedCaptcha')
      }
      this.errors = errors
      if (Object.keys(errors).length) {
        return
      }
      const payload = Object.assign({}, this.form, {
        captchaToken: this.captcha.token,
        exploitEvidenceTypes: this.evidence.join(','),
        impactDimensions: this.dims.join(','),
        attachmentIds: this.files.map((f) => f.id),
        incidentTime: this.form.incidentTime ? this.form.incidentTime.replace('T', ' ') + ':00' : ''
      })
      if (payload.isAnonymous === 1) {
        payload.reporterName = ''
        payload.reporterEmail = ''
      }
      if (payload.incidentTime && payload.incidentTime.length > 19) {
        payload.incidentTime = payload.incidentTime.slice(0, 19)
      }
      portalApi.submit(payload).then((data) => {
        this.trackingNo = data.trackingNo
      }).catch((e) => {
        this.error = e.message || this.t('submitFail')
      })
    }
  }
}
</script>
